import { useCallback } from "react";
import { useGlobalContext } from "../Context/GlobalContext";

const normalizeString = (value) => String(value ?? "").trim();

const requireString = (value, fieldName) => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    throw new Error(`${fieldName} is required`);
  }

  return normalizedValue;
};

const requireCoordinate = (value, fieldName) => {
  const normalizedValue = Number(value);

  if (!Number.isFinite(normalizedValue)) {
    throw new Error(`${fieldName} is required`);
  }

  return normalizedValue;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  return (
    error?.response?.data?.responseMessage ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage
  );
};

export default function useAddress() {
  const { api } = useGlobalContext();

  /* =====================================================
UPDATE ADDRESS LOCATION TAG
===================================================== */

  const updateUserLocationTag = useCallback(
    async ({ locationId, tag }) => {
      try {
        const payload = {
          location_id: requireString(locationId, "Location ID"),
          tag: requireString(tag, "Address tag"),
        };

        console.log("===== updateUserLocationTag Payload =====");
        console.log(payload);

        const response = await api.post(
          "/Order/updateUserLocationTag",
          payload,
        );

        const responseData = response?.data;

        console.log("===== updateUserLocationTag Response =====");
        console.log(responseData);

        if (responseData?.responseCode !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              responseData?.response?.[0]?.message ||
              "Unable to update address tag",
          );
        }

        const result = responseData?.response?.[0];

        if (result?.id === "N") {
          throw new Error(result?.message || "Unable to update address tag");
        }

        return {
          success: true,
          message:
            responseData?.responseMessage || "Address tag updated successfully",
          data: result || null,
          raw: responseData,
        };
      } catch (error) {
        console.error("updateUserLocationTag error:", error);

        throw new Error(
          getApiErrorMessage(error, "Unable to update address tag"),
        );
      }
    },
    [api],
  );

  /* =====================================================
     GET CUSTOMER LIST
  ===================================================== */

  const getCustomerList = useCallback(
    async ({ digit, userid, latitude, longitude }) => {
      try {
        const payload = {
          digit: requireString(digit, "Customer mobile number"),
          userid: "A1001",
          latitude: normalizeString(latitude),
          longitude: normalizeString(longitude),
        };

        console.log("===== getCustomerList Payload =====");
        console.log(payload);

        const response = await api.post("/Order/v2.0/getCustomerList", payload);
        const responseData = response?.data;

        console.log("===== getCustomerList Response =====");
        console.log(responseData);

        if (responseData?.responseCode !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to fetch customer details",
          );
        }

        const responseValue =
          responseData?.response ??
          responseData?.data ??
          responseData?.customers ??
          [];

        const customers = Array.isArray(responseValue)
          ? responseValue
          : responseValue
            ? [responseValue]
            : [];

        return {
          success: true,
          message: responseData?.responseMessage || "",
          customers,
          customer: customers[0] || null,
          raw: responseData,
        };
      } catch (error) {
        console.error("getCustomerList error:", error);
        console.log("Status:", error?.response?.status);
        console.log("Response:", error?.response?.data);
        console.log("Payload:", {
          digit,
          userid,
        });

        throw new Error(
          getApiErrorMessage(error, "Unable to fetch customer details"),
        );
      }
    },
    [api],
  );

  /* =====================================================
     ADD DELIVERY ADDRESS
  ===================================================== */

  const addDeliveryAddress = useCallback(
    async ({
      userid,
      custName,
      custMobile,
      tag,
      address,
      latitude,
      longitude,
    }) => {
      try {
        const payload = {
          userid: requireString(userid, "Restaurant userid"),
          custName: requireString(custName, "Customer name"),
          custMobile: requireString(custMobile, "Customer mobile number"),
          tag: requireString(tag, "Address tag"),
          address: {
            in_pin_code: requireString(address?.in_pin_code, "Pin code"),
            landmark_area: normalizeString(address?.landmark_area),
            address: requireString(address?.address, "Address"),
            city: requireString(address?.city, "City"),
            state: requireString(address?.state, "State"),
          },
          latitude: String(requireCoordinate(latitude, "Latitude")),
          longitude: String(requireCoordinate(longitude, "Longitude")),
        };
        console.log("Add Delivery Address Payload:", payload);

        const response = await api.post(
          "/Order/v3.0/addDeliveryAddress",
          payload,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        console.log("Add Delivery Address Response:", response.data);

        const responseData = response?.data;

        if (responseData?.responseCode !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to add delivery address",
          );
        }

        return {
          success: true,
          message:
            responseData?.responseMessage ||
            "Delivery address added successfully",
          data: responseData?.response ?? responseData?.data ?? null,
          raw: responseData,
        };
      } catch (error) {
        console.error("addDeliveryAddress error:", error);
        console.log("Status:", error?.response?.status);
        console.log("Response:", error?.response?.data);
        console.log("Payload Sent:", payload);

        throw new Error(
          getApiErrorMessage(error, "Unable to add delivery address"),
        );
      }
    },
    [api],
  );

  /* =====================================================
   SET DEFAULT DELIVERY ADDRESS
===================================================== */

  const setDefaultDeliveryAddress = useCallback(
    async ({ userid, address, custMobile }) => {
      try {
        const payload = {
          userid: requireString(userid, "Restaurant userid"),
          address: requireString(address, "Address"),
          custMobile: requireString(custMobile, "Customer mobile number"),
        };

        console.log("Set Default Address Payload:", payload);

        const response = await api.post(
          "/Order/v1.0/setDefaultDeliveryAddress",
          payload,
        );

        const responseData = response?.data;

        console.log("Set Default Address Response:", responseData);

        if (responseData?.responseCode !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to update default address",
          );
        }

        return {
          success: true,
          message:
            responseData?.responseMessage ||
            "Default delivery address updated successfully",
          data: responseData?.response,
          raw: responseData,
        };
      } catch (error) {
        console.error("setDefaultDeliveryAddress error:", error);

        throw new Error(
          getApiErrorMessage(error, "Unable to update default address"),
        );
      }
    },
    [api],
  );

  /* =====================================================
     GET MASTER DELIVERY
  ===================================================== */

  const getMasterDelivery = useCallback(
    async ({ id }) => {
      try {
        const payload = {
          id:"A1223",
        };

        const response = await api.post("/Order/getMasterDelivery", payload, {
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        });

        const responseData = response?.data;
        console.log("getMasterDelivery API Response:", responseData);

        if (responseData?.responseCode !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              "Unable to fetch master delivery details",
          );
        }

        const responseValue =
          responseData?.response ?? responseData?.data ?? [];
        console.log("responseValue:", responseValue);

        const deliveryAddresses = Array.isArray(responseValue)
          ? responseValue
          : responseValue
            ? [responseValue]
            : [];

        return {
          success: true,
          message: responseData?.responseMessage || "",
          deliveryAddresses,
          deliveryAddress: deliveryAddresses[0] || null,
          data: responseValue,
          raw: responseData,
        };
      } catch (error) {
        console.error("getMasterDelivery error:", error);

        throw new Error(
          getApiErrorMessage(error, "Unable to fetch master delivery details"),
        );
      }
    },
    [api],
  );

  /* =====================================================
     DELETE ADDRESS
  ===================================================== */

const deleteUserLocation = useCallback(
  async ({ locationId }) => {
    try {
      const payload = {
  location_id: requireString(
    locationId,
    "Location ID",
  ),
};

      console.log("Delete User Location Payload:", payload);

      const response = await api.post("/Chaafo/deleteUserLocation", payload);

      const responseData = response?.data;

      console.log("Delete User Location Response:", responseData);

    if (responseData?.responseCode !== "00") {
      throw new Error(
        responseData?.responseMessage || "Unable to delete address",
      );
    }

    const deleteResult = responseData?.response?.[0];

    if (deleteResult?.id === "N") {
      throw new Error(deleteResult?.message || "Unable to delete address");
    }

      return {
        success: true,
        message:
          responseData?.responseMessage || "Address deleted successfully",
        data: responseData?.response ?? responseData?.data ?? null,
        raw: responseData,
      };
    } catch (error) {
      console.error("deleteUserLocation error:", error);

      throw new Error(getApiErrorMessage(error, "Unable to delete address"));
    }
  },
  [api],
);


return {
  getCustomerList,
  addDeliveryAddress,
  setDefaultDeliveryAddress,
  getMasterDelivery,
  updateUserLocationTag,
  deleteUserLocation,
};
}
